import { I18n } from '@calendly/i18n';
import { ArrowChevronDownMiniIcon } from '@calendly/ui/components/icons';
import { Loader } from '@calendly/ui/components/loader';
import classNames from 'classnames';
import { useContext, useState, useEffect, forwardRef } from 'react';
import { BaseAttendeeCard } from './base_attendee/base_attendee_card';
import { GuestRow } from './base_attendee/guest_row';
import { ShowMoreButton } from './show_more_button';
import { inviteesCardsStackCss, inviteesStackItemCss, soloInviteeGuestStackCss } from './styles';
import { MeetingDetailDependenciesContext } from '../../../meeting_detail_context';
const INVITEES_TO_SHOW = 10;
const GUESTS_TO_SHOW = 3;
const emptyContainerCss = "e12e3aa3";
const EmptyContainer = forwardRef((props, ref) => {
  const {
    as: AsComponent,
    ...restProps
  } = props;
  const Component = AsComponent || 'div';
  return <Component ref={ref} {...restProps} className={classNames(emptyContainerCss, props.className)} />;
});

/**
 * Single content component for Calendly events (solo and group).
 * Always runs the same hooks (useGetInviteesQuery with skip when solo) and renders
 * the same card type (BaseAttendeeCard) so React hook order stays stable and the
 * render error when switching event types is avoided.
 */
export const CalendlyInviteesContent = ({
  event,
  readonly
}) => {
  const dependencies = useContext(MeetingDetailDependenciesContext);
  if (!dependencies?.useGetInviteesQuery) {
    throw new Error('CalendlyInviteesContent must be wrapped in a MeetingDetailDependenciesProvider with useGetInviteesQuery');
  }
  const isGroup = event.eventType?.kind === 'group';
  const eventId = event.id || event.uuid;
  const [page, setPage] = useState(1);
  const [openPanelKey, setOpenPanelKey] = useState(null);
  const [listExpanded, setListExpanded] = useState(false);
  const [guestsExpanded, setGuestsExpanded] = useState(false);
  const {
    data: inviteesData,
    isLoading,
    isFetching,
    isError,
    error
  } = dependencies.useGetInviteesQuery({
    eventId,
    page
  }, {
    skip: !eventId || !isGroup
  });
  useEffect(() => {
    if (!isGroup || !eventId || !inviteesData || page !== 1) return;
    const results = Array.isArray(inviteesData) ? inviteesData : inviteesData.results ?? [];
    const pagination = Array.isArray(inviteesData) ? {
      totalCount: inviteesData.length,
      currentPage: 1,
      nextPage: false
    } : inviteesData.pagination ?? {
      totalCount: results.length,
      currentPage: 1,
      nextPage: false
    };
    const totalCount = pagination.totalCount ?? results.length;
    if (results.length <= INVITEES_TO_SHOW && (pagination.nextPage || totalCount > INVITEES_TO_SHOW)) {
      setListExpanded(true);
    }
  }, [isGroup, eventId, page, inviteesData]);
  const eventInvitees = event.invitees || [];
  const hasEventInvitees = eventInvitees.length > 0;
  const handleNextPage = () => {
    setPage(prev => prev + 1);
  };
  const handleTogglePanel = key => {
    setOpenPanelKey(prev => prev === key ? null : key);
  };

  // --- Solo: single invitee from event
  if (!isGroup) {
    const invitee = event.invitee;
    const guests = event?.eventGuestEmails || [];
    const guestsToShow = guestsExpanded ? guests : guests.slice(0, GUESTS_TO_SHOW);
    if (!invitee) {
      return null;
    }
    const {
      cancelled,
      newSchedulable,
      confirmed
    } = invitee;
    const isCancelled = cancelled && !newSchedulable;
    const isRescheduled = cancelled && newSchedulable;
    const isConfirmed = !cancelled && confirmed;
    return <div className={guests.length > 0 ? soloInviteeGuestStackCss : undefined}>
        <BaseAttendeeCard event={event} invitee={invitee} readonly={readonly} variant="inline" />
        {guestsToShow.map(email => <GuestRow key={email} email={email} isCancelled={isCancelled} isRescheduled={isRescheduled} isConfirmed={isConfirmed} />)}
        {guests.length > GUESTS_TO_SHOW && !guestsExpanded && <ShowMoreButton event={event} expand={() => setGuestsExpanded(true)}>
            {String(I18n.t('publisher.meetings.invitees.view_more_guests', {
          count: guests.length - GUESTS_TO_SHOW
        }))}
            <ArrowChevronDownMiniIcon />
          </ShowMoreButton>}
      </div>;
  }

  // --- Group: invitees from API (or fallback)
  if (!eventId) {
    return null;
  }
  if (isError && error?.type === 'policy' && hasEventInvitees) {
    const mappedInvitees = eventInvitees.map(inv => ({
      ...inv,
      id: inv.id || inv.uuid
    }));
    const inviteesFromEvent = {
      results: mappedInvitees,
      pagination: {
        totalCount: mappedInvitees.length,
        currentPage: 1,
        nextPage: false
      }
    };
    return <CalendlyInviteesContentList event={event} invitees={inviteesFromEvent} isFetching={false} onNextPage={handleNextPage} readonly={readonly} openPanelKey={openPanelKey} onTogglePanel={handleTogglePanel} listExpanded={listExpanded} onListExpand={() => setListExpanded(true)} onListCollapse={() => setListExpanded(false)} />;
  }
  if (isLoading) {
    return <EmptyContainer data-testid="loader">
        <Loader />
      </EmptyContainer>;
  }
  if (isError) {
    console.error('Error fetching invitees:', error);
    return <EmptyContainer>
        {I18n.t('errors.default_no_action')}
      </EmptyContainer>;
  }
  if (!inviteesData) {
    return null;
  }
  let normalized = Array.isArray(inviteesData) ? {
    results: inviteesData,
    pagination: {
      totalCount: inviteesData.length,
      currentPage: 1,
      nextPage: false
    }
  } : {
    results: inviteesData.results ?? [],
    pagination: inviteesData.pagination ?? {
      totalCount: (inviteesData.results || []).length,
      currentPage: 1,
      nextPage: false
    }
  };
  if (normalized.results.length === 0 && hasEventInvitees) {
    const mapped = eventInvitees.map(inv => ({
      ...inv,
      id: inv.id || inv.uuid
    }));
    normalized = {
      results: mapped,
      pagination: {
        totalCount: mapped.length,
        currentPage: 1,
        nextPage: false
      }
    };
  }
  return <CalendlyInviteesContentList event={event} invitees={normalized} isFetching={isFetching} onNextPage={handleNextPage} readonly={readonly} openPanelKey={openPanelKey} onTogglePanel={handleTogglePanel} listExpanded={listExpanded} onListExpand={() => setListExpanded(true)} onListCollapse={() => setListExpanded(false)} />;
};
function CalendlyInviteesContentList({
  event,
  invitees,
  isFetching,
  onNextPage,
  readonly,
  openPanelKey,
  onTogglePanel,
  listExpanded,
  onListExpand,
  onListCollapse
}) {
  const results = invitees?.results ?? [];
  const pagination = invitees?.pagination ?? {
    totalCount: results.length,
    currentPage: 1,
    nextPage: false
  };
  const inviteesToShow = listExpanded ? results : results.slice(0, INVITEES_TO_SHOW);
  const moreCount = pagination.totalCount - inviteesToShow.length;
  const hasMoreToShow = listExpanded ? pagination.nextPage : pagination.totalCount > INVITEES_TO_SHOW;
  const canCollapse = listExpanded && results.length > INVITEES_TO_SHOW;
  const showMoreButtonText = event.past ? String(I18n.t('publisher.meetings.invitees.view_more_attendees', {
    count: moreCount
  })) : String(I18n.t('publisher.meetings.invitees.view_more_invitees', {
    count: moreCount
  }));
  const showFewerButtonText = event.past ? String(I18n.t('publisher.meetings.invitees.show_fewer_attendees')) : String(I18n.t('publisher.meetings.invitees.show_fewer_invitees'));
  const handleShowMore = () => {
    if (!listExpanded) {
      onListExpand();
    } else {
      onNextPage();
    }
  };
  const handleShowFewer = () => {
    onListCollapse?.();
  };
  return <div>
      <div className={inviteesCardsStackCss}>
        {inviteesToShow.map(invitee => {
        const key = invitee.id ?? invitee.uuid;
        return <div key={key} className={inviteesStackItemCss}>
              <BaseAttendeeCard event={event} invitee={invitee} readonly={readonly} variant="expandable" isExpanded={openPanelKey === key} onToggle={() => onTogglePanel(key)} />
            </div>;
      })}
      </div>
      {hasMoreToShow && <ShowMoreButton disabled={isFetching} event={event} expand={handleShowMore}>
          {isFetching ? <Loader /> : showMoreButtonText}
        </ShowMoreButton>}
      {canCollapse && !pagination.nextPage && <ShowMoreButton event={event} expand={handleShowFewer}>
          {showFewerButtonText}
        </ShowMoreButton>}
    </div>;
}

require("./calendly_invitees_content.wyw-in-js.css!=!../../../../../../../../node_modules/@wyw-in-js/webpack-loader/lib/outputCssLoader.js?cacheProvider=!./calendly_invitees_content.js");